import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores/uiStore';

interface ThemeToggleProps {
  /** Compact mode shows only icon, cycles on click */
  compact?: boolean;
  /** Custom styles for the container */
  style?: React.CSSProperties;
}

/**
 * Theme Toggle Component
 *
 * Two modes:
 * - Compact (drawer): Single icon button that cycles through themes
 * - Full (profile page): Three-button segmented control
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ compact = false, style }) => {
  const { themePreference, cycleTheme, setThemePreference } = useUIStore();
  const { t } = useTranslation('parent');

  const getIcon = () => {
    switch (themePreference) {
      case 'light':
        return <Sun size={18} aria-hidden="true" />;
      case 'dark':
        return <Moon size={18} aria-hidden="true" />;
      case 'system':
        return <Monitor size={18} aria-hidden="true" />;
    }
  };

  const getLabel = () => {
    switch (themePreference) {
      case 'light':
        return t('profile.preferences.themeLightMode');
      case 'dark':
        return t('profile.preferences.themeDarkMode');
      case 'system':
        return t('profile.preferences.themeSystemMode');
    }
  };

  // Compact mode: single icon button
  if (compact) {
    const compactButtonStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      padding: '0.25rem',
      backgroundColor: 'transparent',
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--radius)',
      cursor: 'pointer',
      color: 'var(--text-secondary)',
      transition: 'all 0.2s ease',
      ...style,
    };

    return (
      <button
        style={compactButtonStyle}
        onClick={cycleTheme}
        title={getLabel()}
        aria-label={t('profile.preferences.themeClickToChange', { mode: getLabel() })}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--surface-color)';
          e.currentTarget.style.color = 'var(--text-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--text-secondary)';
        }}
      >
        {getIcon()}
      </button>
    );
  }

  // Full mode: segmented control
  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    overflow: 'hidden',
    ...style,
  };

  const getButtonStyle = (value: 'system' | 'light' | 'dark'): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    minHeight: '36px',
    border: 'none',
    backgroundColor: themePreference === value ? 'var(--primary-color)' : 'var(--background-color)',
    color: themePreference === value ? 'white' : 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: themePreference === value ? '500' : '400',
    transition: 'all 0.2s ease',
  });

  return (
    <div style={containerStyle} role="radiogroup" aria-label={t('profile.preferences.themeAriaLabel')}>
      <button
        type="button"
        style={getButtonStyle('system')}
        onClick={() => setThemePreference('system')}
        role="radio"
        aria-checked={themePreference === 'system'}
      >
        <Monitor size={16} aria-hidden="true" />
        <span>{t('profile.preferences.themeSystem')}</span>
      </button>
      <button
        type="button"
        style={getButtonStyle('light')}
        onClick={() => setThemePreference('light')}
        role="radio"
        aria-checked={themePreference === 'light'}
      >
        <Sun size={16} aria-hidden="true" />
        <span>{t('profile.preferences.themeLight')}</span>
      </button>
      <button
        type="button"
        style={getButtonStyle('dark')}
        onClick={() => setThemePreference('dark')}
        role="radio"
        aria-checked={themePreference === 'dark'}
      >
        <Moon size={16} aria-hidden="true" />
        <span>{t('profile.preferences.themeDark')}</span>
      </button>
    </div>
  );
};
